import React, { useEffect, useRef, useState } from "react";

function getScrollParent(el: Element | null): Element | null {
  let node: Element | null = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const s = getComputedStyle(node);
    const scrolly = /(auto|scroll|overlay)/.test(s.overflowY);
    const scroll = scrolly || /(auto|scroll|overlay)/.test(s.overflow);
    if (scroll) return node;
    node = node.parentElement;
  }
  return null;
}

export default function LazyVisible({
  children,
  rootMargin = "400px 0px",
  threshold = 0.01,
  once = true,
}: {
  children: React.ReactNode;
  rootMargin?: string;
  threshold?: number | number[];
  once?: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show && once) return;
    const el = ref.current;
    if (!el) return;

    if (!(window as any).IntersectionObserver) {
      setShow(true); // old Safari fallback
      return;
    }

    const root = getScrollParent(el); // 👈 use your scroll container if present
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true);
          if (once) io.disconnect();
        }
      },
      { root: root as Element | null, rootMargin, threshold }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [show, rootMargin, threshold, once]);

  // sentinel needs *some* height so Safari sees it
  return <div ref={ref} style={!show ? { minHeight: 1 } : undefined}>{show ? children : null}</div>;
}
